import React, { useState, useEffect } from 'react'
import {
  Modal,
  SelectAsync,
  Select,
  Checkbox,
  Button
} from '@arnsbogroup/react-voicearchive'
import debounce from 'debounce-promise'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useIntl } from 'react-intl'
import messages from './messages'

export default NiceModal.create(({ refetch, orderId = null, dealId = null }) => {
  const { formatMessage } = useIntl()
  const modal = useModal()
  const [customer, setCustomer] = useState(null)
  const [contact, setContact] = useState(null)
  const [contactOptions, setContactOptions] = useState([])
  // Same predicate that decides whether the checkbox is rendered at all, so an
  // order with no deal never asks Bitrix about a deal that cannot exist.
  const [moveDeal, setMoveDeal] = useState(+dealId > 0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (customer) {
      fetch(`/rest/v1/customer/${customer.value}/contacts`)
        .then((response) => response.json())
        .then((items) => setContactOptions(items.map((data) => ({
          value: data.uid,
          label: data.name
        }))))
    }
  }, [customer])

  const loadCustomerOptions = debounce((query) => {
    return new Promise((resolve, reject) => {
      fetch(`/rest/v2/customer?search[name]=${query}`)
        .then(response => response.json())
        .then(({data}) => {
          resolve(data.map(({ uid, name }) => ({
            value: uid,
            label: name
          })))
        })
        .catch(reject)
    })
  }, 250)

  // `force` is only honoured by the backend for the finance role, and is only
  // ever sent after the user has confirmed the warnings it returned.
  const onSubmit = (force = false) => {
    setLoading(true)

    const body = new FormData()
    body.append('orderId', orderId)
    body.append('customerId', customer.value)
    body.append('contactId', contact.value)
    body.append('moveDeal', moveDeal ? '1' : '0')

    if (force) {
      body.append('force', '1')
    }

    fetch(`/order/moveOrder.json`, { method: 'POST', body })
    .then((response) => response.json())
    .then(({success, errors, meta}) => {
      setLoading(false)

      if (success) {
        refetch()
        modal.resolve()
        modal.remove()
        return
      }

      // Finance hit a financial-state warning: let them confirm and retry.
      if (meta?.requiresConfirmation && !force) {
        if (confirm(errors.join('\n\n'))) {
          onSubmit(true)
        }
        return
      }

      // The order moved but a downstream sync (Bitrix) failed. The page is
      // showing stale ownership, so refresh regardless of the error.
      if (meta?.moved) {
        toastr.warning(formatMessage(messages.movedWithWarnings, { errors: errors.join(', ') }))
        refetch()
        modal.resolve()
        modal.remove()
        return
      }

      toastr.error(errors.join(', '))
    }).catch((error) => {
      setLoading(false)
      toastr.error(error)
    })
  }

  return (
    <Modal
      show={modal.visible}
      onClose={() => modal.remove()}
      onOutsideClick={() => modal.remove()}
      onSubmit={() => (!customer || loading || !contact) ? undefined : onSubmit()}
      dialogPanelClasses="overflow-y-visible"
      title={formatMessage(messages.title)}
    >
      <div className="m-4 space-y-4">
        <SelectAsync
          required
          label={formatMessage(messages.selectCustomerLabel)}
          description={formatMessage(messages.selectCustomerDescription)}
          value={customer} 
          onChange={(customer) => { setCustomer(customer) }}
          cacheOptions
          loadOptions={(query) => loadCustomerOptions(query)}
          openMenuOnFocus
          placeholder={formatMessage(messages.selectCustomerPlaceholder)}
        />
        <Select
          required
          disabled={!customer}
          value={contact}
          options={contactOptions}
          onChange={(value) => setContact(value)}
          label={formatMessage(messages.selectContactLabel)}
          description={formatMessage(messages.selectContactDescription)}
          placeholder={formatMessage(messages.selectContactPlaceholder)}
        />
        {+dealId > 0 && (
          <div>
            <Checkbox
              checked={moveDeal}
              onChange={() => setMoveDeal(!moveDeal)}
              label={formatMessage(messages.moveDealLabel)}
            />
            <p className="mt-1 ml-8 text-sm text-gray-500">
              {formatMessage(messages.moveDealDescription)}
            </p>
          </div>
        )}
      </div>

      <Button
        className="float-right"
        size="sm"
        onClick={() => onSubmit()}
        disabled={!customer || loading || !contact}
      >
        {loading ? formatMessage(messages.moveLoading) : formatMessage(messages.moveOrder)}
      </Button>
    </Modal>
  )
})
